import { useMediaQuery } from "@mui/material";
import { traverserStopCellTint, traverserStopRowBorderPulse } from "_shared/_views/highlights/traverser-stop-highlight";
import { traverserStopClassName } from "_shared/_views/highlights/traverser-stop-scope";
import { green } from "@mui/material/colors";
import Typography from "@mui/material/Typography";
import { alpha, styled, useTheme, type SxProps, type Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { MRTNoMemo } from "_shared/_views/tables/table";
import TableAddActions, { type TableAddAction } from "_shared/_views/tables/table-add-actions";
import RowControls, { type RowAction } from "_shared/_views/tables/row-actions";
import SearchMatch from "_shared/_views/tables/search-match";
import {
  resizableColumnOptions,
  useResizableColumns,
} from "_shared/_views/tables/table-column-widths";
import {
  CollectionToolbar,
  CollectionToolbarSearch,
  CollectionToolbarViewButtons,
  searchTextFieldProps,
} from "_shared/_views/tables/table-toolbar";
import {
  FeeScheduleTableCellText,
  feeScheduleTableCellStyles,
  removedTableRowCellStyles,
} from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
} from "material-react-table";
import { useCallback, useMemo, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { paneStretchTable } from "_shared/_views/tables/table-pane-stretch";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";

/**
 * The column that carries a person: their avatar, their name, and any caption beneath it. Named here
 * because the row surround below has to know which cell is the row's leading edge.
 */
const PERSON_COLUMN_ID = "member";
const EMAIL_COLUMN_ID = "email";
const COMPANY_COLUMN_ID = "company";
const ROLE_COLUMN_ID = "role";
/** The edit-lock column, drawn only while the surface says the field is locked. */
const LOCKED_COLUMN_ID = "locked";
const ROW_ACTIONS_COLUMN_ID = "mrt-row-actions";
/**
 * What the trailing controls column is given when the surface does not ask for more.
 *
 * Room for the four an active proposal's roster row can carry — Leave Team, Accept, Reject and the
 * field's own history — at a 32px icon button each, inside the cell's own padding. Every pixel beyond
 * that is a pixel the columns holding the people do not get, and this column takes no share of a wide
 * pane's slack, so it is sized to what it holds rather than rounded up.
 */
const DEFAULT_ACTIONS_COLUMN_WIDTH = 140;
/** One control in a phone's actions column — `RowActions`' buttons and both history controls are 40px. */
const PHONE_ACTION_BUTTON_WIDTH = 40;
/** The cell's own padding either side of them, which `MRTNoMemo` sets for every actions column. */
const PHONE_ACTIONS_CELL_PADDING = 8;
/** The narrowest a reader may drag a person-shaped column, so a drag cannot collapse one to nothing. */
const MINIMUM_PERSON_COLUMN_WIDTH = 60;
/** What the lock column is given: a single glyph, and no share of any slack. */
const LOCKED_COLUMN_WIDTH = 40;
/**
 * What a declared column shows for a row that has nothing to put in it — a member with no email the
 * viewer is entitled to, a person whose firm the directory would not name. A mark, never a fallback
 * identifier: the row's own GUIDs are exactly what must not reach the page (#4830).
 */
const EMPTY_CELL_MARK = "—";
/**
 * How wide each person-shaped column asks to be.
 *
 * These are the widths the columns are laid out at, not caps: the table is drawn in MRT's `grid`
 * layout so that its columns can be resized, and there a column takes the width it declares and shares
 * out any slack in proportion to it. A value longer than its column gets the ellipsis and measured
 * tooltip the cells already carry, and the reader can drag the border or double-click the header to
 * see the whole of it (#4955).
 *
 * The numbers are chosen so that every column fits at the NARROWEST surface a roster has — the Vendor
 * Team tab, where the roster shares its width with the Templates panel and gets about 720px. All four
 * come to {@link TEAM_ROSTER_PERSON_COLUMNS_WIDTH}, which leaves that surface's controls column its
 * own {@link DEFAULT_ACTIONS_COLUMN_WIDTH} and no overflow. A wider pane grows them rather than
 * parking the slack beside the last column.
 */
const COLUMN_WIDTHS: Readonly<Record<TeamRosterColumnId, number>> = {
  // The widest of the four, because it carries the avatar as well as the name: 56px of avatar slot and
  // 32px of cell padding come off it before a single letter is drawn.
  [PERSON_COLUMN_ID]: 195,
  [EMAIL_COLUMN_ID]: 125,
  // Measured against "Vendors R Us, PLLC" — the longest firm name the seeded data carries — plus the
  // cell's own padding.
  [COMPANY_COLUMN_ID]: 149,
  /**
   * Wider than "Leader" or "Member" needs, because a column has to be able to NAME itself: the header
   * carries a sort control (24px) and the `⋮` column menu (24px) beside its text, inside the same 32px
   * of padding. At 80px "Role" came out as "Rol", which is the defect #4955 is answering rather than a
   * new instance of it.
   */
  [ROLE_COLUMN_ID]: 110,
};

/**
 * What the person-shaped columns come to together. Exported for the roster's own spec, which holds the
 * arithmetic above to the narrowest surface's width.
 */
export const TEAM_ROSTER_PERSON_COLUMNS_WIDTH = Object.values(COLUMN_WIDTHS).reduce(
  (total, width) => total + width,
  0,
);

/** The person-shaped columns a roster draws, in the order they are read. */
export const TEAM_ROSTER_PERSON_COLUMNS = [
  PERSON_COLUMN_ID,
  EMAIL_COLUMN_ID,
  COMPANY_COLUMN_ID,
  ROLE_COLUMN_ID,
] as const;

export type TeamRosterColumnId = (typeof TEAM_ROSTER_PERSON_COLUMNS)[number];

/**
 * What the Role column says of the person a team is led by, and of everybody else on it. One
 * vocabulary for all four rosters, so a browser test can pick the leader's row out of a merged table
 * by the same word on every surface.
 */
export const TEAM_LEADER_ROLE = "Leader";
export const TEAM_MEMBER_ROLE = "Member";

/**
 * What a roster draws when it declares no columns — the single identifying column a document
 * collection has always had. At module scope so it is the same array on every render: a fresh literal
 * would rebuild the column definitions each time the table is drawn.
 */
const DEFAULT_COLUMNS: readonly TeamRosterColumnId[] = [PERSON_COLUMN_ID];

/** The same stable empty list on every render, for a roster that offers nothing to add. */
const NO_ADD_ACTIONS: readonly TeamRosterAddAction[] = [];

const COLUMN_HEADERS: Readonly<Record<Exclude<TeamRosterColumnId, "member">, string>> = {
  [EMAIL_COLUMN_ID]: "Email",
  [COMPANY_COLUMN_ID]: "Company",
  [ROLE_COLUMN_ID]: "Role",
};


const Root = styled("section")(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
  position: "relative",
  width: "100%",
  [theme.breakpoints.up("md")]: {
    minWidth: theme.spacing(40),
  },
}));
const PersonCell = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  minWidth: 0,
}));
/**
 * The avatar's place in the Name cell. It rides with the name the way the Fee Schedule puts a
 * category's identity in its first column, and keeps the indent #4533 gave the roster's names.
 */
const RosterAvatarSlot = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  justifyContent: "flex-start",
  marginRight: theme.spacing(1),
  minWidth: theme.spacing(7),
}));
const PersonText = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
}));
const PersonDetail = styled("div")(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "flex",
  flexDirection: "column",
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.43,
}));
const LockedCell = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
}));
/**
 * A row's controls, packed against the cell's right edge (#6041). The leader's row has History alone and
 * a member's has Delete before it, so centred, or packed from the left, the History buttons stood at
 * different places down the column. Against the right edge the trailing control lines up on every row.
 */
const RowActionsContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  justifyContent: "flex-end",
  width: "100%",
}));
const EmptyRoster = styled("div")<{ maxVisibleWidth: number }>(({ maxVisibleWidth, theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  maxWidth: `min(100vw, ${maxVisibleWidth}px)`,
  padding: theme.spacing(5, 0),
  width: "100%",
}));
/**
 * One cell of a roster row beyond the person's name.
 *
 * `searchText` is what the roster's global filter matches the cell on, which is not always what the
 * cell shows: a redline surface flattens the original, revised and current spellings into it so a
 * search still finds an address the current revision struck out — the same thing
 * `getTextRedlineSearchValue` does for a fee category. It falls back to `content` when that is a
 * plain string.
 *
 * `sortText` is what the column ORDERS the cell on, which on those same surfaces is a different
 * question: the flattened string leads with the spelling the current revision struck out, so a cell
 * drawn as `ada@firm.com` would sort under `zoe@firm.com`. A surface whose cell shows one of several
 * spellings passes the one it drew here; a cell whose words are its own needs nothing, and falls back
 * to `searchText` (#5164).
 */
export type TeamRosterCell = {
  content: ReactNode;
  searchText?: string;
  sortText?: string;
};

/** One person on a roster, already rendered by the surface that owns their redline vocabulary. */
export type TeamRosterRow = {
  /**
   * The row's identity, used as the table's row id and as the DOM `id` of the `tr`. Proposal surfaces
   * pass the member's `ProposalField` id so the active-review scroll can still find the row.
   */
  id: string;
  /**
   * Everything the Name column should match this row on. Redline surfaces flatten the original,
   * revised and current spellings into it so a search still finds text a redline struck out.
   */
  searchText: string;
  /**
   * What the Name column ORDERS this row on: the name the row actually draws, without the email and
   * the struck-out spellings {@link searchText} carries. Falls back to `searchText` for a surface
   * whose search string is already the drawn name (#5164).
   */
  sortText?: string;
  /** The person's avatar, drawn inside the Name cell. The surface colours it; this table never interprets it. */
  avatar: ReactNode;
  /** The person's name line. */
  name: ReactNode;
  /** A caption under the name — a "Resolved" note, a document's size. Not the person's email; that is a column. */
  detail?: ReactNode;
  /** The Email column's cell. Drawn only while the roster declares that column. */
  email?: TeamRosterCell;
  /** The Company column's cell — the firm this person sits at, which need not be the leader's. */
  company?: TeamRosterCell;
  /** The Role column's cell — what this person is to the team: its leader, or one of its members. */
  role?: TeamRosterCell;
  /** The row's trailing controls, in the order the surface wants them read. */
  actions?: ReactNode;
  /**
   * The same controls as data, for a phone, where two or more fold into one menu the way every list
   * page's do and a single one stays a button (#5907). A surface that supplies this or
   * {@link trailingAction} is drawn from them on a phone and from {@link actions} everywhere else; one
   * that supplies neither keeps {@link actions} at every width.
   */
  menuActions?: readonly RowAction[];
  /**
   * Drawn after {@link menuActions} on a phone and never folded into the menu — the row's field
   * history, which opens a popover of its own and carries a change badge.
   */
  trailingAction?: ReactNode;
  /** Extra attributes for the `tr`, such as the `data-removed-member` a departed member carries. */
  rowAttributes?: Readonly<Record<string, string>>;
  /**
   * Extra styling for this person's row. The draft roster draws `pendingChangeTableRowStyles` through
   * it — a table paints a row's background on its cells, not on the `tr`, so the list's
   * `pendingChangeListItemStyles` would land on an element that shows nothing.
   *
   * A single style object or one theme callback rather than `SxProps` at large: it is folded into the
   * row's own styling below, and MUI's array form of `sx` holds no nested arrays.
   */
  rowSx?: SystemStyleObject<Theme> | ((theme: Theme) => SystemStyleObject<Theme>);
  /** Draws the pulsing outline that marks the field a review is currently sitting on. */
  isActive?: boolean;
  /** Draws the green surround that marks a redline decided and accepted on this row. */
  isResolvedAccepted?: boolean;
  /**
   * Draws the row as a removed person: every person column struck through in the disabled tone, the
   * way a removed document or fee schedule category is, and the avatar grayed (#6995). Never the
   * controls, which still hold the row's Undo and history.
   */
  isRemoved?: boolean;
  /** Called for a click on the row that did not land on one of its controls. */
  onClick?: () => void;
};

/**
 * One "add someone to this roster" affordance. The shared shape, because a roster's adds are drawn by
 * the same split button every collection's are — see `table-add-actions`.
 */
export type TeamRosterAddAction = TableAddAction;

export type TeamRosterTableProps = {
  className?: string;
  /**
   * The roster's name — "Team Roster". The table's accessible name and its search field's placeholder,
   * but not drawn as a heading: the tab the roster sits on already names the team, and a heading over
   * the only table on it cost a line and said nothing more.
   */
  title: string;
  /**
   * Which surface this roster is, stamped on the section as `data-roster-surface`.
   *
   * Every roster carries the same name now that a team is one table rather than two, so the
   * accessible name no longer tells the active proposal's redline roster from the draft's, the
   * archive's, or the Client Team tab's. A browser test names the surface it means through this.
   *
   * The data attributes are how a browser test addresses a roster without reaching into an emotion
   * class name: `data-roster-table` marks the whole column — any notice, the toolbar and the table —
   * and `data-roster-surface` says which roster it is. The first is deliberately not `section`, which
   * the proposal page's own layout already uses several levels up.
   */
  surface?: string;
  /**
   * A note drawn above the table. The draft roster says here why an actor who may
   * still edit the roster has no leader controls (#4854) — above the people rather than inside the
   * table, where an empty roster would swallow it.
   */
  notice?: ReactNode;
  rows: readonly TeamRosterRow[];
  /**
   * The collection's "add one" affordances, in the order they are read — "Select Leader", "Add
   * Member", "Upload Client Policy Document". A list rather than one, because a team is one table now:
   * the draft roster offers a leader and a member, and they are governed by different permissions
   * (#4854), so either can be present without the other.
   */
  addActions?: readonly TeamRosterAddAction[];
  /**
   * An action on the roster as a whole that is not a way of adding to it, drawn as its own button
   * after the add control — a proposal tab's Save as Template (#5808).
   */
  standaloneAction?: TableAddAction;
  /**
   * What the roster says when it holds nobody. A node rather than a wording where the roster is not
   * empty but unread — the team tabs hand it their own loader.
   */
  emptyMessage: ReactNode;
  /** What the roster says when a search matched nobody. Defaults to `emptyMessage`. */
  noMatchesMessage?: ReactNode;
  /** The person column's header. Defaults to "Name". */
  personColumnHeader?: string;
  /**
   * Which columns this roster draws beside the person's name. Defaults to the person column alone,
   * which is the shape a document collection wants; a team roster passes
   * {@link TEAM_ROSTER_PERSON_COLUMNS}.
   */
  columns?: readonly TeamRosterColumnId[];
  /**
   * The mark drawn in a leading column of its own while the field is locked to another actor — the
   * Fee Schedule's `locked` column. Absent, the column is not drawn at all.
   *
   * A node rather than a tooltip wording, because the mark itself belongs to the proposals domain and
   * this table is shared: the surface hands in its own `ReviewerEditLockIcon`. It is a column rather
   * than a swapped-out avatar, which is what it used to be, because the avatar carries the redline
   * colour ladder — replacing it hid the very signal the reviewer is reading the roster for.
   */
  lockIndicator?: ReactNode;
  /** Offers a search field over the row's columns. Off by default: a one-person roster has nothing to search. */
  enableSearch?: boolean;
  /**
   * How much room the trailing controls column asks for. A roster row carries two or three controls; a
   * document row carries up to six (replace, remove, undo, accept, reject, history), which wrap onto a
   * second line at the roster's width.
   */
  actionsColumnWidth?: number;
};

/**
 * A roster-shaped collection drawn as a table, with the Fee Schedule's columns, cells and mechanics:
 * accessor columns whose value is a flattened search string, `feeScheduleTableCellStyles`' body2
 * ellipsis-and-tooltip treatment, row actions pinned right, a paper-sized empty state, and a visible
 * horizontal scrollbar where the columns run past the pane.
 *
 * Its columns are resizable: drag a header's edge, or double-click (or double-tap) the header to swap
 * between the column's full content width and the width the table gives it (#4955).
 *
 * Built for the team rosters (#4955) and since widened to the proposal's document collections —
 * Conflict Documents, Client Policies, Vendor Policies — which are the same shape: an icon, a name a
 * redline may have struck through, a caption beneath it, and per-row redline controls. Those pass no
 * `columns`, so they keep the single identifying column they have always had; a team roster passes
 * {@link TEAM_ROSTER_PERSON_COLUMNS} and gets Name, Email, Company and Role.
 *
 * Carries the Fee Schedule's whole toolbar too — search, Toggle filters, Show/Hide columns, Toggle
 * full screen — and gives every data column MRT's own `⋮` column-actions menu, so Name, Email, Company
 * and Role sort and filter the way a category's columns do (#4955). These had been suppressed in the
 * name of #4895, which was a misreading of it: what #4895 fixed was a Popover that opened BY ITSELF,
 * when the field-history Prev/Next traversal auto-opened a field's history and marked the rest of the
 * page `aria-hidden` with no user action at all. A menu a reader deliberately clicks open is an
 * ordinary transient dialog — the same thing as the confirm dialogs these surfaces already raise on
 * Remove — and it gives the page back the moment it is dismissed. The property worth protecting is
 * that NO surface here opens a modal of its own accord and blinds the page; the absence of MRT's
 * toolbar was never that property. `team-roster-table.spec.tsx` guards the property itself.
 */
export default function TeamRosterTable(props: Readonly<TeamRosterTableProps>) {
  const { className, title, surface, rows, emptyMessage } = props;
  const personColumnHeader = props.personColumnHeader ?? "Name";
  const enableSearch = props.enableSearch ?? false;
  const actionsColumnWidth = props.actionsColumnWidth ?? DEFAULT_ACTIONS_COLUMN_WIDTH;
  const declaredColumns = props.columns ?? DEFAULT_COLUMNS;
  const addActions = props.addActions ?? NO_ADD_ACTIONS;
  const standaloneAction = props.standaloneAction;
  const lockIndicator = props.lockIndicator;
  const noMatchesMessage = props.noMatchesMessage ?? emptyMessage;

  const theme = useTheme();
  // Hoisted into a const for two reasons. It is a computed key in the cell styling below, and the
  // React Compiler cannot lower a call in that position — it would drop this whole component's
  // memoization silently (see app/CLAUDE.md). And `useMediaQuery`'s callback form resolves its own
  // theme, so a roster rendered outside a `ThemeProvider` — which every one of these specs is — would
  // be handed `null`.
  const mediumDownQuery = theme.breakpoints.down("md");
  // The Fee Schedule squeezes five columns onto a phone by dropping to MRT's compact density, and a
  // roster now carries the same five. There is no density toggle beside it — that is one more of
  // MRT's `Menu`-backed controls — so the display size decides on the reader's behalf.
  const isMediumDisplaySize = useMediaQuery(mediumDownQuery);
  const phoneQuery = theme.breakpoints.down("sm");
  const isPhone = useMediaQuery(phoneQuery);
  // The scroll container is the roster's own, handed to MRT below. The rows that run past its right
  // edge are followed with the scrollbar `MRTNoMemo` draws on it — there are no arrows of the roster's
  // own any more, and no shaded edges: they sat in a strip of their own under the table, which on a
  // paged table stacked a second row of chevrons under the paginator's (#4955).
  const columnWidths = useResizableColumns<TeamRosterRow>();
  const { containerRef: tableContainerRef, headCellProps: widthHeadCellProps, bodyCellProps: widthBodyCellProps } =
    columnWidths;

  const columnHelper = useMemo(() => createMRTColumnHelper<TeamRosterRow>(), []);
  const enableRowActions = rows.some(
    (row) => hasNode(row.actions) || (row.menuActions?.length ?? 0) > 0 || hasNode(row.trailingAction),
  );
  // On a phone the column is as wide as the widest row's controls once its menu has folded, rather than
  // the room a desktop row's four icons need: one person's history alone sat in 140px, the column wider
  // than the name it hid (#5907). Only where every row describes its controls as data — a row that
  // hands in a finished node cannot be counted, so a roster with one keeps the surface's own width.
  const phoneActionsColumnWidth = useMemo(() => {
    const rowsWithActions = rows.filter(
      (row) => hasNode(row.actions) || row.menuActions !== undefined || hasNode(row.trailingAction),
    );
    if (rowsWithActions.some((row) => row.menuActions === undefined && !hasNode(row.trailingAction))) {
      return undefined;
    }
    const widestRow = Math.max(1, ...rowsWithActions.map(phoneActionButtonCount));
    return widestRow * PHONE_ACTION_BUTTON_WIDTH + PHONE_ACTIONS_CELL_PADDING;
  }, [rows]);
  const effectiveActionsColumnWidth = isPhone ? (phoneActionsColumnWidth ?? actionsColumnWidth) : actionsColumnWidth;
  const showLockedColumn = lockIndicator !== undefined && lockIndicator !== null;

  const columns = useMemo(() => {
    const definitions: MRT_ColumnDef<TeamRosterRow, string>[] = [];

    if (showLockedColumn) {
      definitions.push(
        columnHelper.display({
          header: "",
          id: LOCKED_COLUMN_ID,
          size: LOCKED_COLUMN_WIDTH,
          minSize: LOCKED_COLUMN_WIDTH,
          // A single glyph takes no share of the pane's slack, and is not worth dragging the edge of.
          grow: false,
          enableResizing: false,
          enableColumnActions: false,
          enableSorting: false,
          muiTableBodyCellProps: { align: "center", sx: { verticalAlign: "middle" } },
          Cell: () => <LockedCell>{lockIndicator}</LockedCell>,
        }) as MRT_ColumnDef<TeamRosterRow, string>,
      );
    }

    for (const columnId of declaredColumns) {
      if (columnId === PERSON_COLUMN_ID) {
        definitions.push(
          columnHelper.accessor((row: TeamRosterRow) => row.searchText, {
            header: personColumnHeader,
            id: PERSON_COLUMN_ID,
            size: COLUMN_WIDTHS[PERSON_COLUMN_ID],
            minSize: MINIMUM_PERSON_COLUMN_WIDTH,
            enableGlobalFilter: true,
            filterFn: "includesString",
            // The accessor is the SEARCH string, which is not what the column is ordered by: see
            // `rosterRowSortText`.
            sortingFn: (rowA, rowB) =>
              compareRosterText(rosterRowSortText(rowA.original), rosterRowSortText(rowB.original)),
            Cell: ({ row, table }: { row: MRT_Row<TeamRosterRow>; table: MRT_TableInstance<TeamRosterRow> }) => (
              <PersonCell>
                <RosterAvatarSlot data-roster-avatar-slot="">{row.original.avatar}</RosterAvatarSlot>
                <PersonText>
                  {renderPersonText(row.original.name, table)}
                  {row.original.detail !== undefined && row.original.detail !== null && (
                    <PersonDetail>{renderPersonText(row.original.detail, table)}</PersonDetail>
                  )}
                </PersonText>
              </PersonCell>
            ),
          }),
        );
        continue;
      }

      definitions.push(
        columnHelper.accessor((row: TeamRosterRow) => rosterCellSearchText(row[columnId]), {
          header: COLUMN_HEADERS[columnId],
          id: columnId,
          size: COLUMN_WIDTHS[columnId],
          minSize: MINIMUM_PERSON_COLUMN_WIDTH,
          enableGlobalFilter: true,
          filterFn: "includesString",
          sortingFn: (rowA, rowB) =>
            compareRosterText(rosterCellSortText(rowA.original[columnId]), rosterCellSortText(rowB.original[columnId])),
          Cell: ({ row, table }: { row: MRT_Row<TeamRosterRow>; table: MRT_TableInstance<TeamRosterRow> }) =>
            renderRosterCell(row.original[columnId], table),
        }),
      );
    }

    return definitions;
  }, [columnHelper, declaredColumns, lockIndicator, personColumnHeader, showLockedColumn]);

  const renderRowActions = useCallback(
    (actionProps: { row: MRT_Row<TeamRosterRow> }) => {
      const row = actionProps.row.original;
      if (isPhone && (row.menuActions !== undefined || hasNode(row.trailingAction))) {
        const menuActions = row.menuActions ?? [];
        return (
          <RowActionsContainer>
            <RowControls
              actions={menuActions}
              collapsed={menuActions.length > 1}
              ariaLabel={`Actions for ${row.sortText ?? row.searchText}`}
              trailing={row.trailingAction}
            />
          </RowActionsContainer>
        );
      }
      return <RowActionsContainer>{row.actions}</RowActionsContainer>;
    },
    [isPhone],
  );

  const getRowId = useCallback((row: TeamRosterRow) => row.id, []);

  const muiTableProps = useMemo(
    () => ({
      "aria-label": title,
      sx: { borderSpacing: 0 },
    }),
    [title],
  );

  const muiTableBodyRowProps = useCallback(
    (rowProps: { row: MRT_Row<TeamRosterRow> }) => {
      const row = rowProps.row.original;
      const handleRowClick = row.onClick;
      const rowStyles = {
        cursor: handleRowClick ? "pointer" : "default",
        ...(row.isActive && { animation: traverserStopRowBorderPulse }),
      };
      // A caller's own `rowSx` is folded in here rather than handed to MUI as a second entry of an
      // `sx` array: the array form reaches the row as a list emotion composes separately, and the
      // row's animation stopped being readable off it.
      const extraRowStyles = row.rowSx;
      const sx: SxProps<Theme> =
        typeof extraRowStyles === "function"
          ? (rowTheme: Theme) => ({ ...rowStyles, ...extraRowStyles(rowTheme) })
          : { ...rowStyles, ...extraRowStyles };
      return {
        // The stop's highlight, and what the page scrolls to — see `traverser-stop-scope`.
        className: traverserStopClassName(row.isActive),
        hover: false,
        id: row.id,
        ...row.rowAttributes,
        onClick: (event: ReactMouseEvent<HTMLTableRowElement>) => {
          if (!handleRowClick) return;
          const target = event.target;
          // A click that landed on one of the row's own controls belongs to that control. Without this
          // the row's click handler would fire behind every button in it.
          if (
            target instanceof Element &&
            target.closest(
              "button, a, input, textarea, select, [role='button'], [contenteditable='true'], .MuiFormControl-root, .MuiInputBase-root",
            )
          ) {
            return;
          }
          handleRowClick();
        },
        sx,
      };
    },
    [],
  );

  const muiTableBodyCellProps = useCallback(
    (cellProps: { cell: { column: { id: string } }; row: MRT_Row<TeamRosterRow> }) => {
      const columnId = cellProps.cell.column.id;
      const row = cellProps.row.original;
      // The row-level surrounds are drawn across the person-shaped columns, never the lock mark or the
      // controls — exactly the set the Fee Schedule boxes.
      const isSurroundedCell = declaredColumns.includes(columnId as TeamRosterColumnId);
      const isLeadingCell = columnId === declaredColumns[0];
      const isTrailingCell = columnId === declaredColumns.at(-1);
      return {
        ...widthBodyCellProps(columnId),
        sx: {
          // The Fee Schedule's own cell treatment, verbatim: body2, one line, an ellipsis, and a
          // tooltip that appears only where the text is actually cut off. Adopting it is what makes a
          // roster cell read as a cell rather than as a list entry wearing a `td`.
          ...feeScheduleTableCellStyles(theme, { columnId }),
          // The pinned controls column floats over whatever it has scrolled past, and MRT's own
          // backdrop for it is only about 0.97 alpha — so the column underneath read THROUGH the
          // trash and history icons, which is how "Role" came out as "R le" (#4955). A pinned column
          // that is not opaque is not a pinned column. Stated as the cell's own background rather
          // than by overriding MRT's, so a row that paints itself — the pending-change highlight
          // reaches the cells through `& > td`, which outranks this — still wins.
          ...(columnId === ROW_ACTIONS_COLUMN_ID && { backgroundColor: theme.palette.background.paper }),
          verticalAlign: "middle",
          [mediumDownQuery]: {
            padding: theme.spacing(0.5, 1),
          },
          // The row-level "decided and accepted" surround. A roster row is one person and one
          // decision, so — unlike a fee category, which is negotiated column by column — it is drawn
          // once around the whole row rather than per cell.
          ...(row.isResolvedAccepted &&
            isSurroundedCell && {
            backgroundColor: alpha(green[500], 0.06),
            borderTop: `1px solid ${green[500]}`,
            borderBottom: `1px solid ${green[500]}`,
            ...(isLeadingCell && { borderLeft: `1px solid ${green[500]}` }),
            ...(isTrailingCell && { borderRight: `1px solid ${green[500]}` }),
          }),
          ...(row.isRemoved &&
            isSurroundedCell && {
            ...removedTableRowCellStyles(theme),
            "& [data-roster-avatar-slot] .MuiAvatar-root": {
              filter: "grayscale(1)",
              opacity: 0.6,
            },
          }),
          ...(row.isActive &&
            isSurroundedCell && {
            borderTop: "2px solid",
            borderBottom: "2px solid",
            ...(isLeadingCell && { borderLeft: "2px solid" }),
            ...(isTrailingCell && { borderRight: "2px solid" }),
            borderColor: "inherit",
            ...traverserStopCellTint,
          }),
        },
      };
    },
    [declaredColumns, mediumDownQuery, theme, widthBodyCellProps],
  );

  /**
   * The header row's own three jobs: an opaque backdrop under the pinned controls column, the column's
   * name so its cells can be found and measured, and the double-click that toggles that column between
   * its full content width and the width the table gives it.
   */
  const muiTableHeadCellProps = useCallback(
    (headProps: { column: { id: string }; table: MRT_TableInstance<TeamRosterRow> }) => ({
      ...widthHeadCellProps(headProps.column.id, headProps.table),
      sx: {
        ...(headProps.column.id === ROW_ACTIONS_COLUMN_ID && {
          backgroundColor: theme.palette.background.paper,
          borderBottom: "none",
        }),
      },
    }),
    [theme, widthHeadCellProps],
  );

  const renderEmptyRowsFallback = useCallback(
    (fallbackProps: { table: MRT_TableInstance<TeamRosterRow> }) => {
      const searching = Boolean(fallbackProps.table.getState().globalFilter);
      const message = searching ? noMatchesMessage : emptyMessage;
      return (
        <EmptyRoster maxVisibleWidth={fallbackProps.table.refs.tablePaperRef.current?.clientWidth ?? 360}>
          {/* A plain wording is the roster's own sentence and is typed as one; anything else — a
              loader, say — is drawn exactly as the surface built it. */}
          {typeof message === "string" ? (
            <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="h6">
              {message}
            </Typography>
          ) : (
            message
          )}
        </EmptyRoster>
      );
    },
    [emptyMessage, noMatchesMessage],
  );

  // The search first, the add control beside it, and the three view buttons at the far end — one
  // wrapping row, so the add control sits to the RIGHT of the search wherever there is room for it and
  // drops underneath where there is not (#4955). The several add affordances a roster can offer are
  // one split button rather than a row of equal ones.
  const renderTopToolbarCustomActions = useCallback(
    (toolbarProps: { table: MRT_TableInstance<TeamRosterRow> }) => (
      <CollectionToolbar>
        {enableSearch && (
          <CollectionToolbarSearch>
            <GlobalFilterTextField table={toolbarProps.table} />
          </CollectionToolbarSearch>
        )}
        <TableAddActions actions={addActions} standaloneActions={standaloneAction ? [standaloneAction] : undefined} />
        <CollectionToolbarViewButtons>
          <TableToolsMenu table={toolbarProps.table} />
        </CollectionToolbarViewButtons>
      </CollectionToolbar>
    ),
    [addActions, enableSearch, standaloneAction],
  );

  // The whole toolbar is drawn in the custom-actions slot above, never in the one below — which is
  // where a wide page would normally put it. MRT lays that slot out as its own grid cell sized from
  // the toolbar, and the toolbar clips what does not fit with no scrollbar to say so: a roster is the
  // widest table on the page, so a search box and three buttons in that cell ran off the right-hand
  // edge and took two of the three buttons and half the pinned controls column with them (#4955).
  //
  // An empty fragment rather than `null`: MRT reads this result with `??`, so a nullish return falls
  // straight back to its own default buttons and the page would carry two sets of them.
  const renderToolbarInternalActions = useCallback(() => <></>, []);

  const muiTableContainerProps = useMemo(
    () => ({
      ref: tableContainerRef,
      // `overflowX: auto` is what makes the remaining overflow reachable at all: every ancestor of
      // this table clips, so without it a column pushed past the viewport is simply gone (#4490).
      // `MRTNoMemo` draws the scrollbar itself, so that the reader can see there is more that way.
      sx: { minWidth: 0, overflowX: "auto", position: "relative" },
    }),
    [tableContainerRef],
  );

  const tableOptions: MRT_TableOptions<TeamRosterRow> = useMemo(
    () => ({
      // Resizable columns, and the `grid` layout they need: a semantic table sizes its columns from
      // their content whatever a drag says, so a handle there moves nothing. See
      // `table-column-widths`.
      ...resizableColumnOptions<TeamRosterRow>(),
      columns,
      data: rows as TeamRosterRow[],
      getRowId,
      enableRowActions,
      // `enableColumnActions` is deliberately unset: MRT's default is on, and the Fee Schedule beside
      // these rosters simply omits it too. A column's `⋮` menu opens only because a reader clicked it,
      // and closes on the next click anywhere — see the class comment for why that is not what #4895
      // was about.
      enableColumnPinning: true,
      enableColumnFilters: true,
      enableGlobalFilter: enableSearch,
      enableGlobalFilterRankedResults: false,
      globalFilterFn: "includesString",
      enableKeyboardShortcuts: false,
      enablePagination: false,
      enableBottomToolbar: false,
      enableTableFooter: false,
      // Always on, because the toolbar always holds something now: the three view controls are offered
      // on every roster, including one with nothing to add and nothing to search. Sorting Name, showing
      // fewer columns and reading a roster full-screen are as much use on a team nobody may edit as on
      // one they may, and none of them writes anything.
      enableTopToolbar: true,
      // The roster is short and always shown whole: MRT is handed every row and asked not to page it.
      // `manualSorting` is deliberately unset, though — with it the Name / Email / Company / Role sort
      // items moved the header arrow and left the rows exactly where they were.
      manualPagination: true,
      positionGlobalFilter: "none",
      initialState: {
        columnPinning: { right: [ROW_ACTIONS_COLUMN_ID] },
        // The per-column filter row is offered, not imposed: Toggle filters opens it.
        showColumnFilters: false,
        showGlobalFilter: enableSearch,
      },
      state: { density: isMediumDisplaySize ? "compact" : "comfortable" },
      displayColumnDefOptions: {
        [ROW_ACTIONS_COLUMN_ID]: {
          header: "",
          size: effectiveActionsColumnWidth,
          minSize: effectiveActionsColumnWidth,
          // The controls column takes no share of a wide pane's slack and is not resizable: it is
          // sized to the controls the surface puts in it, and a reader dragging it wider would only
          // take width off the columns that carry the people.
          grow: false,
          enableResizing: false,
        },
      },
      muiTableProps,
      muiTableBodyRowProps,
      muiTableBodyCellProps,
      muiTableHeadCellProps,
      // MRT lays the top toolbar out as a grid whose inner flex Box keeps `min-width: auto`, so the
      // track sizes itself to that Box's CONTENT — and the toolbar, being `overflow: hidden`, then
      // clips whatever ends up past the right edge with no scrollbar to say so. A zero floor is what
      // lets the track clamp to the pane instead, which is what the toolbar's one wrapping row needs
      // in order to wrap at all. See docs/software-design/table-toolbars.md.
      muiTopToolbarProps: { sx: { "& > .MuiBox-root": { minWidth: 0 } } },
      muiTablePaperProps: { ...paneStretchTable, elevation: 0, sx: { backgroundColor: "transparent" } },
      muiTableContainerProps,
      // The app's one search box: the icon, a placeholder naming what is searched, and a clear
      // button. The roster hand-rolled the placeholder and took none of the rest, which left the
      // team tabs as the only proposal tabs whose search had no icon in it.
      muiSearchTextFieldProps: ({ table }) => ({
        ...searchTextFieldProps({ searches: title, table }),
        size: "small" as const,
        variant: "outlined" as const,
      }),
      renderEmptyRowsFallback,
      renderRowActions,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
    }),
    [
      columns,
      effectiveActionsColumnWidth,
      enableRowActions,
      enableSearch,
      getRowId,
      isMediumDisplaySize,
      muiTableBodyCellProps,
      muiTableBodyRowProps,
      muiTableContainerProps,
      muiTableHeadCellProps,
      muiTableProps,
      renderEmptyRowsFallback,
      renderRowActions,
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      rows,
      title,
    ],
  );

  return (
    <Root className={className} data-roster-table="" data-roster-surface={surface}>
      {props.notice}
      <MRTNoMemo<TeamRosterRow> options={tableOptions} />
    </Root>
  );
}

/** Whether a slot holds something to draw — `false` and `null` are what a `cond && <X />` leaves behind. */
function hasNode(node: ReactNode): boolean {
  return node !== undefined && node !== null && node !== false;
}

/** How many 40px controls a row shows on a phone: its menu folded to one button where it has two or more. */
function phoneActionButtonCount(row: TeamRosterRow): number {
  const menuCount = row.menuActions?.length ?? 0;
  return Math.min(menuCount, 1) + (hasNode(row.trailingAction) ? 1 : 0);
}

/**
 * One plain-text cell beyond the name, on the Fee Schedule's own body2 / ellipsis /
 * measured-tooltip treatment.
 *
 * A value the viewer has none of — an email their entitlement withholds, a firm the directory would
 * not name — is left with no content, so the column draws {@link EMPTY_CELL_MARK} rather than a
 * stand-in built from the row's identifiers, which name nobody to a reader (#4830). Exported so all
 * three rosters that build such cells decide that one way.
 */
export function teamRosterCell(value: string | undefined): TeamRosterCell {
  if (!value) return { content: undefined, searchText: "" };
  return { content: <FeeScheduleTableCellText title={value}>{value}</FeeScheduleTableCellText>, searchText: value };
}

/**
 * What the global filter matches a cell on: the search string the surface flattened for it, or the
 * cell's own wording when it is plain text. A cell built from nodes alone contributes nothing, which
 * is the point — an unresolved row is searchable by the words it shows, never by the identifiers it
 * deliberately withholds (#4830).
 */
function rosterCellSearchText(cell: TeamRosterCell | undefined): string {
  if (!cell) return "";
  if (cell.searchText !== undefined) return cell.searchText;
  if (typeof cell.content === "string") return cell.content;
  if (typeof cell.content === "number") return String(cell.content);
  return "";
}

/**
 * What the Name column orders a row on: the name the row draws, or — for a surface whose search
 * string IS the drawn name — that string.
 *
 * The two are the same question on the draft roster and different ones on a redline surface, where
 * the search string deliberately holds every spelling the negotiation has carried at once. Ordering
 * on that string sorts a renamed member under the spelling that was struck out, and breaks ties
 * between unchanged rows on the email appended after the name rather than on the surname (#5164).
 */
function rosterRowSortText(row: TeamRosterRow): string {
  return row.sortText ?? row.searchText;
}

/** The same question for a cell beyond the name: what it drew, falling back to what it is searched on. */
function rosterCellSortText(cell: TeamRosterCell | undefined): string {
  return cell?.sortText ?? rosterCellSearchText(cell);
}

/**
 * How one roster value is ordered against another — case- and accent-insensitive, the way the
 * permissions matrix orders the names in its own rows. A reader sorting a team by Name is asking for
 * an alphabet, not for capitals before lowercase.
 */
function compareRosterText(left: string, right: string): number {
  return left.localeCompare(right, undefined, { sensitivity: "base" });
}

/**
 * One non-name cell. A plain wording goes through the Fee Schedule's own cell text — body2, one line,
 * an ellipsis, and a tooltip only where the text is actually cut off — and anything richer is drawn
 * exactly as the surface built it, on top of the same cell styling.
 */
function renderRosterCell(cell: TeamRosterCell | undefined, table: MRT_TableInstance<TeamRosterRow>): ReactNode {
  const content = cell?.content;
  if (content === undefined || content === null || content === "") {
    return <FeeScheduleTableCellText color="text.disabled">{EMPTY_CELL_MARK}</FeeScheduleTableCellText>;
  }
  if (typeof content === "string" || typeof content === "number") {
    // The search covers every column, so a plain wording marks it (#6590). The tooltip is named, since
    // the marked text is no longer a string the cell can read it from.
    return (
      <FeeScheduleTableCellText title={String(content)}>
        <SearchMatch table={table} text={String(content)} />
      </FeeScheduleTableCellText>
    );
  }
  return content;
}

/**
 * A name or caption line, marked where the roster's search matches it when the surface handed over
 * plain text (#6590). A node the surface built — a profile link, a redline — is drawn as it came.
 */
function renderPersonText(content: ReactNode, table: MRT_TableInstance<TeamRosterRow>): ReactNode {
  return typeof content === "string" ? <SearchMatch table={table} text={content} /> : content;
}
